class ShopTheLookPopupModal extends HTMLElement {
  constructor() {
    super();
    this.close = this.close.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
  }

  connectedCallback() {
    // Cache references to key elements
    this.backdrop = this.querySelector('.shop-the-look-popup-modal__backdrop');
    this.closeBtn = this.querySelector('.shop-the-look-popup-modal__close-btn');
    this.mainImageContainer = this.querySelector('.shop-the-look-popup-modal__main-image');
    this.popupTitleElement = this.querySelector('.shop-the-look-popup-modal__popup-title');
    this.itemTitleElement = this.querySelector('.shop-the-look-popup-modal__item-title');
    this.subtitleElement = this.querySelector('.shop-the-look-popup-modal__subtitle');
    this.headerElement = this.querySelector('.shop-the-look-popup-modal__header');
    this.productsContainer = this.querySelector('.shop-the-look-popup-modal__products');

    // Wire up event listeners
    this.closeBtn.addEventListener('click', this.close);
    this.backdrop.addEventListener('click', this.onBackdropClick);
    document.addEventListener('keydown', this.handleEscape);

    // Listen for clicks on buttons with data-modal-trigger="shop-the-look-popup"
    document.addEventListener('click', this.handleTriggerClick.bind(this));
  }

  disconnectedCallback() {
    // Clean up listeners
    this.closeBtn.removeEventListener('click', this.close);
    this.backdrop.removeEventListener('click', this.onBackdropClick);
    document.removeEventListener('keydown', this.handleEscape);
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest('[data-modal-trigger="shop-the-look-popup"]');
    if (!trigger) return;

    // If clicking on an image link that also has a product URL, prevent default navigation
    if (trigger.tagName === 'A' && trigger.href) {
      event.preventDefault();
    }
    event.stopPropagation();

    // Get the block data from the trigger's parent item
    const item = trigger.closest('.shop-the-look-popup__item');
    if (!item) return;

    // Get block ID from the item's data attributes
    const blockId = item.getAttribute('data-block-id');
    if (!blockId) return;

    // Open modal with the block item
    this.open(item);
  }

  open(blockItem) {
    if (!blockItem) return;

    // Extract block image - try multiple sources
    let blockImageSrc = null;
    const imageContainer = blockItem.querySelector('.shop-the-look-popup__image');
    if (imageContainer) {
      // Try to get from img tag first
      const img = imageContainer.querySelector('img');
      if (img) {
        // Prefer src, fallback to srcset
        blockImageSrc = img.src;
        if (!blockImageSrc && img.srcset) {
          const srcsetParts = img.srcset.split(',');
          if (srcsetParts.length > 0) {
            // Get the largest image from srcset (last one usually)
            const lastSrc = srcsetParts[srcsetParts.length - 1].trim().split(' ')[0];
            blockImageSrc = lastSrc;
          }
        }
      } else {
        // Try to get from picture source
        const sources = imageContainer.querySelectorAll('source');
        if (sources.length > 0) {
          // Get the last source (usually the largest/default)
          const lastSource = sources[sources.length - 1];
          const srcset = lastSource.getAttribute('srcset');
          if (srcset) {
            const srcsetParts = srcset.split(',');
            if (srcsetParts.length > 0) {
              blockImageSrc = srcsetParts[srcsetParts.length - 1].trim().split(' ')[0];
            }
          }
        }
      }
    }

    // Get popup title from block settings
    const popupTitle = blockItem.getAttribute('data-popup-title') || '';
    
    // Get item title and subtitle from block settings
    const itemTitle = blockItem.getAttribute('data-item-title') || '';
    const itemSubtitle = blockItem.getAttribute('data-item-subtitle') || '';

    // Get look products from data attributes
    const lookProducts = this.extractLookProducts(blockItem);

    // Populate modal content
    this.populateModal(blockImageSrc, popupTitle, itemTitle, itemSubtitle, lookProducts);

    // Show modal
    this.backdrop.setAttribute('open', '');
    document.body.classList.add('modal-open');
    this.closeBtn.focus();
  }

  extractLookProducts(blockItem) {
    const products = [];
    
    // Look for product data attributes on the block item
    for (let i = 1; i <= 5; i++) {
      const productHandle = blockItem.getAttribute(`data-product-${i}-handle`);
      const productImage = blockItem.getAttribute(`data-product-${i}-image`);
      const productTitle = blockItem.getAttribute(`data-product-${i}-title`);
      const productUrl = blockItem.getAttribute(`data-product-${i}-url`);
      const productPrice = blockItem.getAttribute(`data-product-${i}-price`);
      const productCompareAtPrice = blockItem.getAttribute(`data-product-${i}-compare-at-price`);
      const productColor = blockItem.getAttribute(`data-product-${i}-color`);

      // Add product if we have at least a handle, image, or title
      if (productHandle || productImage || productTitle) {
        const product = {
          handle: productHandle || '',
          image: productImage || '',
          title: productTitle || '',
          color: productColor || '',
          url: productUrl || (productHandle ? `/products/${productHandle}` : '#'),
          price: productPrice || '',
          compareAtPrice: productCompareAtPrice || ''
        };
        
        products.push(product);
      }
    }

    return products;
  }

  populateModal(blockImageSrc, popupTitle, itemTitle, itemSubtitle, lookProducts) {
    // Set main image
    if (blockImageSrc) {
      this.mainImageContainer.innerHTML = `
        <img src="${blockImageSrc}" alt="${itemTitle || 'Product image'}" loading="lazy">
      `;
    } else {
      this.mainImageContainer.innerHTML = '';
    }

    // Set popup title (top left)
    if (popupTitle && popupTitle.trim() !== '') {
      this.popupTitleElement.textContent = popupTitle;
      this.popupTitleElement.style.display = 'block';
    } else {
      this.popupTitleElement.textContent = '';
      this.popupTitleElement.style.display = 'none';
    }

    // Set item title and subtitle (top right)
    this.itemTitleElement.textContent = itemTitle || '';
    
    // Set subtitle with truncation toggle on mobile
    if (!itemSubtitle) {
      this.subtitleElement.style.display = 'none';
      this.subtitleElement.innerHTML = '';
    } else {
      this.subtitleElement.style.display = 'block';
      this.subtitleElement.innerHTML = `
        <span class="shop-the-look-popup-modal__subtitle-text">${this.escapeHtml(itemSubtitle)}</span>
        <button class="shop-the-look-popup-modal__subtitle-toggle" aria-label="Toggle subtitle" type="button">
          <span class="shop-the-look-popup-modal__subtitle-toggle-text">SHOW MORE</span>
          <svg class="shop-the-look-popup-modal__subtitle-icon" role="presentation" focusable="false" width="8" height="6" viewBox="0 0 8 6">
            <path d="m1 1.5 3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.5"></path>
          </svg>
        </button>
      `;
      
      // Set up toggle functionality
      this.setupSubtitleToggle();
    }
    
    // Remove header margin if both title and subtitle are empty
    if (!itemTitle && !itemSubtitle) {
      this.headerElement.style.marginBottom = '0';
    } else {
      this.headerElement.style.marginBottom = '';
    }

    // Populate products column
    if (lookProducts.length > 0) {
      this.productsContainer.innerHTML = lookProducts.map(product => {
        const imageUrl = product.image || '';
        const productUrl = product.url || '#';
        const productTitle = product.title || '';
        const productColor = product.color || '';
        const productPrice = product.price || '';
        const productCompareAtPrice = product.compareAtPrice || '';
        const isOnSale = productCompareAtPrice && productCompareAtPrice !== '0' && productCompareAtPrice !== '' && 
                         parseFloat(productCompareAtPrice) > parseFloat(productPrice);

        // Always show product info section with product title, color, and price
        // Use look product image if available, otherwise show placeholder
        const displayImage = imageUrl || '';
        
        // Build price HTML using price-list structure
        let priceHtml = '';
        if (productPrice && productPrice !== '0' && productPrice !== '') {
          // Calculate discount badge if on sale
          let badgeHtml = '';
          if (isOnSale && window.themeVariables && window.themeVariables.settings.showDiscount) {
            const compareAtPriceNum = parseFloat(productCompareAtPrice);
            const priceNum = parseFloat(productPrice);
            const discountMode = window.themeVariables.settings.discountMode || 'saving';
            
            let savings = '';
            if (discountMode === 'percentage') {
              const percentage = Math.round(((compareAtPriceNum - priceNum) / compareAtPriceNum) * 100);
              savings = `${percentage}%`;
            } else {
              const savingsAmount = compareAtPriceNum - priceNum;
              savings = this.formatPrice(savingsAmount);
            }
            
            badgeHtml = `
              <div class="product-info__badge-list">
                <on-sale-badge class="badge badge--on-sale">Save ${savings}</on-sale-badge>
              </div>
            `;
          }
          
          if (isOnSale) {
            priceHtml = `
              <price-list class="price-list shop-the-look-popup-modal__product-price-list">
                <compare-at-price class="text-subdued line-through">${this.formatPrice(productCompareAtPrice)}</compare-at-price>
                <sale-price class="text-on-sale">${this.formatPrice(productPrice)}</sale-price>
                ${badgeHtml}
              </price-list>
            `;
          } else {
            priceHtml = `
              <price-list class="price-list shop-the-look-popup-modal__product-price-list">
                <sale-price class="text-subdued">${this.formatPrice(productPrice)}</sale-price>
              </price-list>
            `;
          }
        }
        
        return `
          <div class="shop-the-look-popup-modal__product">
            <div class="shop-the-look-popup-modal__product-content">
              ${displayImage ? `<a href="${productUrl}" class="shop-the-look-popup-modal__product-image-link">
                <div class="shop-the-look-popup-modal__product-image">
                  <img src="${displayImage}" alt="${productTitle || 'Product'}" loading="lazy">
                </div>
              </a>` : '<div class="shop-the-look-popup-modal__product-image shop-the-look-popup-modal__product-image--placeholder"></div>'}
              <div class="shop-the-look-popup-modal__product-info">
                ${productTitle ? `<a href="${productUrl}" class="shop-the-look-popup-modal__product-title-link">
                  <span class="shop-the-look-popup-modal__product-title">${this.escapeHtml(productTitle)}</span>
                </a>` : ''}
                ${productColor ? `<span class="shop-the-look-popup-modal__product-color">${this.escapeHtml(productColor)}</span>` : ''}
                ${priceHtml}
              </div>
            </div>
          </div>
        `;
      }).join('');
    } else {
      this.productsContainer.innerHTML = '<p>No products available</p>';
    }
  }

  formatPrice(priceInCents) {
    // Convert cents to dollars and format
    const dollars = parseFloat(priceInCents) / 100;
    return `$${dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2)}`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  setupSubtitleToggle() {
    const toggleBtn = this.subtitleElement.querySelector('.shop-the-look-popup-modal__subtitle-toggle');
    const subtitleText = this.subtitleElement.querySelector('.shop-the-look-popup-modal__subtitle-text');
    const icon = this.subtitleElement.querySelector('.shop-the-look-popup-modal__subtitle-icon');
    const toggleText = this.subtitleElement.querySelector('.shop-the-look-popup-modal__subtitle-toggle-text');
    
    if (!toggleBtn || !subtitleText) return;
    
    // Check if we're on mobile
    const isMobile = window.matchMedia('(max-width: 699px)').matches;
    if (!isMobile) {
      // Not on mobile, don't show toggle button
      return;
    }
    
    // Check if text is more than 150 characters
    const textContent = subtitleText.textContent || subtitleText.innerText || '';
    const textLength = textContent.length;
    
    if (textLength > 150) {
      // Text is longer than 150 characters, show toggle button and set initial state to truncated
      toggleBtn.classList.add('show');
      this.subtitleElement.classList.add('shop-the-look-popup-modal__subtitle--truncated');
      
      toggleBtn.addEventListener('click', () => {
        const isTruncated = this.subtitleElement.classList.contains('shop-the-look-popup-modal__subtitle--truncated');
        
        if (isTruncated) {
          // Expand: show full text
          this.subtitleElement.classList.remove('shop-the-look-popup-modal__subtitle--truncated');
          if (toggleText) toggleText.textContent = 'SHOW LESS';
          if (icon) icon.style.transform = 'rotate(180deg)';
        } else {
          // Collapse: show truncated text
          this.subtitleElement.classList.add('shop-the-look-popup-modal__subtitle--truncated');
          if (toggleText) toggleText.textContent = 'SHOW MORE';
          if (icon) icon.style.transform = 'rotate(0deg)';
        }
      });
    } else {
      // Text is 150 characters or less, don't show toggle button
      toggleBtn.classList.remove('show');
      this.subtitleElement.classList.remove('shop-the-look-popup-modal__subtitle--truncated');
    }
  }

  onBackdropClick(event) {
    // Only close if the click was directly on the backdrop, not inside the modal
    if (event.target === this.backdrop) {
      this.close();
    }
  }

  close() {
    this.backdrop.removeAttribute('open');
    document.body.classList.remove('modal-open');
    
    // Clear content after animation
    setTimeout(() => {
      this.mainImageContainer.innerHTML = '';
      this.productsContainer.innerHTML = '';
      this.popupTitleElement.textContent = '';
      this.itemTitleElement.textContent = '';
      this.subtitleElement.textContent = '';
      if (this.headerElement) {
        this.headerElement.style.marginBottom = '';
      }
    }, 300);
  }

  handleEscape(e) {
    if (e.key === 'Escape' && this.backdrop.hasAttribute('open')) {
      this.close();
    }
  }
}

if (!customElements.get('shop-the-look-popup-modal')) {
  customElements.define('shop-the-look-popup-modal', ShopTheLookPopupModal);
}

